import React, { createContext, useContext, useState } from 'react';
import { validateEmail } from '../utils/validators';

const AuthContext = createContext();
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try { const s = localStorage.getItem('dhs-user'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [loading] = useState(false);
  // Kept for backwards compatibility with any component that still references it — always null now that OTPs are server-side.
  const [pendingOtp, setPendingOtp] = useState(null);

  /**
   * Passwordless customer sign-in / sign-up. Sends a real 6-digit code
   * via Resend to the given email. `payload` (name etc.) is remembered
   * locally so we can complete the registration mirror after verification.
   */
  const startEmailOtp = async (email, purpose = 'login', payload = null) => {
    const v = validateEmail(email);
    if (!v.ok) return { ok: false, error: v.error };
    try {
      const res = await fetch(`${BACKEND}/api/auth/customer/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: v.formatted,
          name: payload?.firstName ? `${payload.firstName} ${payload.lastName || ''}`.trim() : null,
          login_only: purpose === 'login',
        }),
      });
      const j = await res.json();
      if (!res.ok) return { ok: false, error: j.detail || 'Could not send code' };
      setPendingOtp({ email: v.formatted, purpose, payload });
      return { ok: true };
    } catch { return { ok: false, error: 'Network error, please try again.' }; }
  };

  /**
   * Verifies the OTP against the backend. On success returns
   * `{ ok, email, purpose, payload, token, name }`.
   */
  const verifyOtp = async (code) => {
    if (!pendingOtp) return { ok: false, error: 'No verification in progress' };
    try {
      const res = await fetch(`${BACKEND}/api/auth/customer/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pendingOtp.email, code: (code || '').trim() }),
      });
      const j = await res.json();
      if (!res.ok) return { ok: false, error: j.detail || 'Incorrect or expired code' };
      return { ok: true, purpose: pendingOtp.purpose, email: pendingOtp.email, payload: pendingOtp.payload, token: j.token, name: j.name };
    } catch { return { ok: false, error: 'Network error, please try again.' }; }
  };

  const login = (email /*, password */) => {
    // Passwordless — instruct the caller to switch to the OTP flow.
    // Kept for backwards compatibility with older callers.
    return startEmailOtp(email, 'login');
  };

  /**
   * Completes registration locally after OTP verification succeeded.
   * The backend already knows the account exists; here we just mirror the
   * profile so the existing UI can display it.
   */
  const register = (payload, token) => {
    const email = payload.email.toLowerCase();
    const users = JSON.parse(localStorage.getItem('dhs-users') || '[]');
    let existing = users.find((u) => u.email === email);
    if (!existing) {
      existing = {
        id: 'u-' + Date.now(), createdAt: new Date().toISOString(),
        policyNumber: 'DHS-' + Math.floor(100000 + Math.random() * 900000),
        plan: 'Home Cover Plan', role: 'customer',
        ...payload,
        email,
      };
      users.push(existing);
      localStorage.setItem('dhs-users', JSON.stringify(users));
    }
    const safe = { ...existing, token };
    localStorage.setItem('dhs-user', JSON.stringify(safe));
    setUser(safe);
    try {
      const list = JSON.parse(localStorage.getItem('dhs-customers') || '[]');
      if (!list.find((c) => c.email === safe.email)) {
        list.unshift({ ...safe, status: 'Active' });
        localStorage.setItem('dhs-customers', JSON.stringify(list));
      }
    } catch {}
    setPendingOtp(null);
    return { ok: true, user: safe };
  };

  const signInAfterOtp = (email, token, name) => {
    const users = JSON.parse(localStorage.getItem('dhs-users') || '[]');
    const found = users.find((u) => u.email === email.toLowerCase());
    const safe = { ...(found || { email: email.toLowerCase(), firstName: (name || email.split('@')[0]), role: 'customer' }), token };
    localStorage.setItem('dhs-user', JSON.stringify(safe));
    setUser(safe);
    setPendingOtp(null);
    return { ok: true, user: safe };
  };

  const logout = () => { localStorage.removeItem('dhs-user'); setUser(null); };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, startEmailOtp, verifyOtp, signInAfterOtp,
      pendingOtp, clearPendingOtp: () => setPendingOtp(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
